import { useEffect, useMemo } from "react";
import { stripUndefinedValues } from "utils/utils";
import { z } from "zod";

import { useRecentViews } from "components/saved_searches/utils";
import { useSavedViewsDataType } from "lib/hooks/api/saved_views/useSavedViews";
import { CurrentUserData, useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useURLState } from "lib/hooks/useURLState";
import { createSavedViewSelectedEvent } from "lib/savedViewTracking";
import { EventNames, useTracking } from "lib/tracking";
import {
  DocumentCategory,
  SavedViewDataType,
  SavedViewSortBy,
  SavedViewSortOrder,
} from "lib/types/graphQLEnums";
import { SavedDocumentView } from "lib/types/savedViews";
import {
  areFiltersEqual,
  collapseDocumentCategories,
  DEFAULT_DOCUMENT_FILTERS,
  DocumentFilters,
  parseDocumentsUrlState,
  SortField,
} from "./utils";

let lastSetRecentViewId: string | null = null; // Module level on purpose

function hasDocumentsUrlState(state: unknown): boolean {
  return typeof state === "object" && state !== null;
}

function convertViewCategories(
  viewCategories: string[] | undefined | null,
  user: CurrentUserData | undefined,
) {
  if (!viewCategories) return undefined;

  const convertedViewCategories = viewCategories
    .map((c) =>
      Object.values(DocumentCategory).includes(c as DocumentCategory)
        ? (c as DocumentCategory)
        : undefined,
    )
    .filter((c): c is DocumentCategory => Boolean(c));

  return collapseDocumentCategories(convertedViewCategories, user);
}

function convertViewFiltersToDocumentFilters(
  view: SavedDocumentView["view"],
  user: CurrentUserData | undefined,
): DocumentFilters {
  const {
    signalScore,
    title,
    organisationIds,
    buyerTypes,
    buyerListIds,
    buyerCountryRegions,
    keywords,
    publishDate,
    categories,
  } = view.filterSettings;
  const { field, order } = view.sortOrder;
  const documentFilters: DocumentFilters = {
    signalScore,
    title,
    organisationIds: organisationIds ?? undefined,
    buyerTypes: buyerTypes ?? undefined,
    buyerListIds: buyerListIds ?? undefined,
    buyerCountryRegions: buyerCountryRegions
      ? {
          countries: buyerCountryRegions.countries ?? [],
          regions: buyerCountryRegions.regions ?? [],
          selected: buyerCountryRegions.selected ?? [],
        }
      : undefined,
    keywords: keywords ?? [],
    categories: convertViewCategories(categories, user) || [],
    publishDate: publishDate
      ? {
          filter: {
            from: publishDate?.filter?.from ?? undefined,
            to: publishDate?.filter?.to ?? undefined,
            relativeFrom: publishDate?.filter?.relativeFrom ?? undefined,
            relativeTo: publishDate?.filter?.relativeTo ?? undefined,
          },
        }
      : undefined,
    sort: {
      field: field as SortField,
      order: order as "ASC" | "DESC",
    },
  };

  return documentFilters;
}

export function useSavedViews(defaultFilters?: DocumentFilters, isEnabled: boolean = true) {
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();

  const { data: allDocumentSavedViewsQuery } = useSavedViewsDataType(
    SavedViewDataType.Document,
    {},
    { page: 1, pageSize: 1000 },
    { enabled: isEnabled },
  );
  const allDocumentSavedViews = allDocumentSavedViewsQuery?.results;

  const { data: pinnedDocumentViewsQuery } = useSavedViewsDataType(
    SavedViewDataType.Document,
    { pinned: true, orderBy: SavedViewSortBy.PinnedRank, order: SavedViewSortOrder.Asc },
    { page: 1, pageSize: 50 },
    { enabled: isEnabled },
  );
  const pinnedDocumentViews = pinnedDocumentViewsQuery?.results;

  const { recentViewsIds, setRecentViewsIds, recentViews } = useRecentViews(
    SavedViewDataType.Document,
    isEnabled,
  );

  const [urlFilters, setUrlFilters] = useURLState<DocumentFilters | undefined>(
    "documentFilters",
    defaultFilters ?? DEFAULT_DOCUMENT_FILTERS,
    parseDocumentsUrlState,
  );

  // The URL drops empty filters, so filters cleared back to the defaults are indistinguishable
  // from no URL filters by value alone; only a missing param should fall back to the saved view.
  const [hasUrlFilters] = useURLState("documentFilters", false, hasDocumentsUrlState);

  const [, setShowNoViewReplaceState] = useURLState(
    "showNoView",
    false,
    z.union([z.boolean(), z.stringbool()]).optional(),
    { replaceState: true },
  );

  const [showNoView, setShowNoView] = useURLState(
    "showNoView",
    false,
    z.union([z.boolean(), z.stringbool()]).optional(),
    { replaceState: false },
  );

  const [urlViewId, setUrlViewId] = useURLState(
    "current_view_id",
    undefined,
    z.string().optional(),
  );
  const urlView =
    pinnedDocumentViews?.find((v) => v.id == urlViewId) ??
    allDocumentSavedViews?.find((v) => v.id === urlViewId);
  const selectedView = showNoView ? undefined : urlView || recentViews[0];

  const effectiveDefaultFilters = defaultFilters ?? DEFAULT_DOCUMENT_FILTERS;
  // This deals with the case where a user is coming from a link with custom filters
  const hasCustomDefaults =
    defaultFilters && !areFiltersEqual(defaultFilters, DEFAULT_DOCUMENT_FILTERS);

  const filters = useMemo(() => {
    // If we have URL filters, use them, even when they've been cleared back to the defaults
    if (urlFilters && hasUrlFilters) {
      return urlFilters;
    }
    // If custom default filters were passed, prioritize them over saved views
    if (hasCustomDefaults) {
      return effectiveDefaultFilters;
    }
    // If we have a selected view, use its filters
    if (selectedView) {
      return convertViewFiltersToDocumentFilters(selectedView.view, currentUser);
    }
    // Otherwise, use default filters
    return effectiveDefaultFilters;
  }, [
    urlFilters,
    hasUrlFilters,
    selectedView,
    effectiveDefaultFilters,
    hasCustomDefaults,
    currentUser,
  ]);

  const setSelectedView = (viewId?: string, viewName?: string, context?: string) => {
    if (!viewId) {
      setUrlViewId(undefined);
      setUrlFilters(DEFAULT_DOCUMENT_FILTERS);
      setShowNoViewReplaceState(true);
      return;
    }

    setUrlViewId(viewId);
    // Remove filter parameters from URL when selecting a view
    setUrlFilters(undefined);
    setShowNoViewReplaceState(undefined);
    logEvent(
      EventNames.viewSelected,
      createSavedViewSelectedEvent("document", viewId, viewName, context),
    );
  };

  useEffect(() => {
    if (
      isEnabled &&
      urlViewId &&
      !showNoView &&
      recentViewsIds[0] !== urlViewId &&
      lastSetRecentViewId !== urlViewId
    ) {
      // Module level variable to avoid the recent view being set multiple times due to multiple hook instances
      // and to prevent infinite loops from occurring if a bug is introduced that causes an infinite loop
      lastSetRecentViewId = urlViewId;

      // Add view to recent views or moves it to the top if already exists
      setRecentViewsIds([urlViewId, ...recentViewsIds.filter((v) => v !== urlViewId)]);
    }
  }, [isEnabled, urlViewId, showNoView, recentViewsIds, setRecentViewsIds]);

  const filtersHaveChanged = () => {
    // Check the url and confirm if there is only the param current_view_id
    const url = window.location.href;
    const urlParams = new URLSearchParams(url);
    if (urlView && urlParams.size === 1) {
      return false;
    }

    // If there are URL filters and a current view, check if the filters have changed
    if (urlFilters && urlView) {
      const convertedViewFilters = stripUndefinedValues(
        convertViewFiltersToDocumentFilters(urlView.view, currentUser),
      );

      return !areFiltersEqual(urlFilters, convertedViewFilters);
    }

    // If there are URL filters and no current view, check if the filters have changed
    if (urlFilters && !urlView) {
      // If urlFilters equals effectiveDefaultFilters, filters haven't changed from defaults
      return !areFiltersEqual(urlFilters, effectiveDefaultFilters);
    }

    // If there are no URL filters and a current view, return false
    if (!urlFilters && urlView) {
      return false;
    }

    // If there are no URL filters and no current view, check if custom defaults were passed
    if (!urlFilters && !urlView && hasCustomDefaults) {
      return true;
    }

    // If there are no URL filters and no current view return false
    return false;
  };

  const getAppliedFiltersCount = (filters: DocumentFilters) => {
    const isObjectEmpty = (obj: unknown): boolean => {
      if (typeof obj !== "object" || obj === null) {
        return false;
      }
      // Check if object has any non-empty properties
      for (const value of Object.values(obj)) {
        if (value !== undefined && value !== null) {
          // If it's an array, check if it has items
          if (Array.isArray(value)) {
            if (value.length > 0) return false;
          }
          // If it's a string, check if it's not empty
          else if (typeof value === "string") {
            if (value.length > 0) return false;
          }
          // If it's an object, recursively check if it's empty
          else if (typeof value === "object") {
            if (!isObjectEmpty(value)) return false;
          }
          // For other types (boolean, number), it's not empty
          else {
            return false;
          }
        }
      }
      return true;
    };

    return Object.entries(filters).reduce<number>((acc, [key, val]) => {
      // Exclude the "sort" object from the count
      if (key === "sort") {
        return acc;
      }

      if (Array.isArray(val)) {
        return acc + (val.length > 0 ? 1 : 0);
      }

      if (typeof val === "string") {
        return acc + (val.length > 0 ? 1 : 0);
      }

      if (typeof val === "boolean") {
        return acc + 1;
      }

      if (typeof val === "object" && val !== null) {
        return acc + (isObjectEmpty(val) ? 0 : 1);
      }

      return acc + (val ? 1 : 0);
    }, 0);
  };

  // Check if filters are customized (either current filters differ from defaults, or custom defaults were provided)
  const hasCustomFilters =
    !areFiltersEqual(filters, effectiveDefaultFilters) ||
    (defaultFilters && !areFiltersEqual(defaultFilters, DEFAULT_DOCUMENT_FILTERS));

  const viewOrCustomFiltersApplied =
    Boolean(selectedView) || filtersHaveChanged() || hasCustomFilters;

  return {
    allDocumentSavedViews,
    selectedView,
    setSelectedView,
    setUrlViewId,
    filters,
    setFilters: setUrlFilters,
    filtersHaveChanged,
    recentViews,
    getAppliedFiltersCount,
    showNoView,
    setShowNoView,
    hasCustomFilters,
    viewOrCustomFiltersApplied,
  };
}
